import { getMomentTz, MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Carrier } from "../models/Carrier";
import {
	CarrierForm,
	CarrierFormResponse,
	FormInstanceData,
} from "../models/CarrierForm";
import { PassHistoryEntry } from "../models/Passes";
import { EdgerRollPassResponse } from "@/pages/catalogs/models/EdgerRoll";
import { parseEdgerRollPasses } from "@/pages/catalogs/pages/edger-rolls/components/passes/PassesGrid";

export interface AffectedPassChange {
	CarrierInstancePassId: number;
	EdgerRollPartId: number;
	EdgerRollName: string;
	PassNumber: number;
	StartedAt: Date | null;
	EndedAt: Date | null;
}

export interface PassChangeSummary {
	EdgerRollPartId: number;
	EdgerRollName: string;
	AffectedCount: number;
	TotalActiveCount: number;
}

export interface PassChangePreview {
	affectedPasses: AffectedPassChange[];
	summary: PassChangeSummary[];
}

const moment = getMomentTz();

function isISOString(value: any) {
	if (typeof value !== "string") return false;

	// Regex para formato ISO 8601 completo
	const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
	return isoRegex.test(value);
}

export const getCarrierFormInstance = async (
	params: { partId?: number | null; carrierInstanceId?: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params.partId) parameters.push({ name: "PartId", value: params.partId });
	if (params.carrierInstanceId)
		parameters.push({
			name: "CarrierInstanceId",
			value: params.carrierInstanceId,
		});

	const resp = await apiService.callV2(
		"[M2].[GetCarrierFormInstance]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let carrierData = get(resp, "data.tables[0].rows[0]", []) as CarrierForm;

		carrierData = {
			...carrierData,
			CreatedAt: carrierData.CreatedAt
				? moment.utc(carrierData.CreatedAt).toDate()
				: null,
			DisassembledAt: carrierData.DisassembledAt
				? moment.utc(carrierData.DisassembledAt).toDate()
				: null,
			InstalledAt: carrierData.InstalledAt
				? moment.utc(carrierData.InstalledAt).toDate()
				: null,
			SignedOffAt: carrierData.SignedOffAt
				? moment.utc(carrierData.SignedOffAt).toDate()
				: null,
			UninstalledAt: carrierData.UninstalledAt
				? moment.utc(carrierData.UninstalledAt).toDate()
				: null,
			UpdatedAt: carrierData.UpdatedAt
				? moment.utc(carrierData.UpdatedAt).toDate()
				: null,
		};

		const formData = get(resp, "data.tables[1].rows", []) as FormInstanceData[];
		const passesData = get(
			resp,
			"data.tables[2].rows",
			[],
		) as EdgerRollPassResponse[];

		return {
			carrierData,
			formData,
			passesData: {
				top: parseEdgerRollPasses(
					passesData.filter((p) => p.EdgerRollPartId == carrierData.TopRollId),
				),
				bottom: parseEdgerRollPasses(
					passesData.filter(
						(p) => p.EdgerRollPartId == carrierData.BottomRollId,
					),
				),
			},
		} as CarrierFormResponse;
	} else {
		throw new Error(resp.message || "Error fetching carriers");
	}
};

export const getCarriers = async (
	params: { partId?: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	if (params.partId) parameters.push({ name: "PartId", value: params.partId });

	const resp = await apiService.callV2(
		"[M2].[GetCarriers]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Carrier[];
	} else {
		throw new Error(resp.message || "Error fetching carriers");
	}
};

export const getLastCarrierInstances = async (
	params: { partId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "PartId", value: params.partId }];

	const resp = await apiService.callV2(
		"[M2].[GetLastCarrierInstances]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Carrier[];
	} else {
		throw new Error(resp.message || "Error fetching carrier instances");
	}
};

type assembleCarrierProps = {
	partId: number;
	field: string;
	value: boolean | string | number | Date | null;
	force: boolean;
	bottomRollId?: number | null;
};

export const assemblyCarrier = async ({
	partId,
	field,
	value,
	force,
}: assembleCarrierProps) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "PartId", value: partId },
		{ name: field, value: value || null },
		{ name: "Force", value: force ? 1 : 0 },
	];
	const resp = await apiService.callV2("[M2].[AssembleCarrier]", parameters);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting carriers");
	}
};

export const previewPassChangesOutsideWindow = async ({
	carrierInstanceId,
	proposedInstalledAt,
	proposedUninstalledAt,
}: {
	carrierInstanceId: number;
	proposedInstalledAt?: Date;
	proposedUninstalledAt?: Date;
}): Promise<PassChangePreview> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "CarrierInstanceId", value: carrierInstanceId },
	];
	if (proposedInstalledAt) {
		parameters.push({
			name: "ProposedInstalledAt",
			value: proposedInstalledAt.toISOString(),
		});
	}
	if (proposedUninstalledAt) {
		parameters.push({
			name: "ProposedUninstalledAt",
			value: proposedUninstalledAt.toISOString(),
		});
	}

	const resp = await apiService.callV2(
		"[M2].[PreviewPassChangesOutsideWindow]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error previewing pass changes");
	}

	const affectedPasses = (
		get(resp, "data.tables[0].rows", []) as AffectedPassChange[]
	).map((row) => ({
		...row,
		StartedAt: row.StartedAt ? moment.utc(row.StartedAt).toDate() : null,
		EndedAt: row.EndedAt ? moment.utc(row.EndedAt).toDate() : null,
	}));

	const summary = get(resp, "data.tables[1].rows", []) as PassChangeSummary[];

	return { affectedPasses, summary };
};

export const updateCarrierStatus = async ({
	partId,
	newStatus,
	operatorId,
	comments,
	timestamp,
	forceDeletePasses,
}: {
	partId: number;
	newStatus: string;
	operatorId?: number | null;
	comments?: string | null;
	timestamp?: Date | null;
	forceDeletePasses?: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "PartId", value: partId },
		{ name: "NewStatusCode", value: newStatus },
	];

	if (operatorId) {
		parameters.push({ name: "SignOffOperatorId", value: operatorId });
	}
	if (comments) {
		parameters.push({ name: "SignOffComments", value: comments });
	}
	if (timestamp) {
		parameters.push({ name: "Timestamp", value: timestamp.toISOString() });
	}
	if (forceDeletePasses) {
		parameters.push({ name: "ForceDeletePasses", value: true });
	}

	const resp = await apiService.callV2(
		"[M2].[UpdateCarrierStatus]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating status");
	}

	const message = get(
		resp,
		"data.outputParameters.@AUTOADJUSTMENTMESSAGES",
		null,
	);

	if (resp.ok && message) {
		return message;
	}
};

export const processMillBuilding = async ({
	partId,
	rollsetId,
	questionKey,
	value,
}: {
	partId: number;
	rollsetId?: number | null;
	questionKey?: string | null;
	value?: string | null;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "PartId", value: partId }];

	if (rollsetId) {
		parameters.push({ name: "RollsetId", value: rollsetId });
	}
	if (questionKey) {
		parameters.push({ name: "QuestionKey", value: questionKey });
	}
	if (value) {
		if (isISOString(value)) {
			const formattedDate = value.replace("T", " ").replace(/\.\d{3}Z$/, "");
			parameters.push({ name: "Value", value: formattedDate });
		} else {
			parameters.push({ name: "Value", value: value });
		}
	}

	const resp = await apiService.callV2(
		"[M2].[ProcessMillBuilding]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving form");
	}
};

export const answerAll = async ({ partId }: { partId: number | null }) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "PartId", value: partId }];

	const resp = await apiService.callV2("[M2].[AnswerCarrierForm]", parameters);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving form");
	}
};

export const upsertCarrierInstancePass = async ({
	carrierInstancePassId,
	carrierInstanceId,
	passNumber,
	startedAt,
}: {
	carrierInstancePassId?: number;
	carrierInstanceId: number;
	passNumber: number;
	startedAt: Date;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "CarrierInstanceId", value: carrierInstanceId },
		{ name: "PassNumber", value: passNumber },
		{ name: "StartedAt", value: startedAt.toISOString() },
	];

	if (carrierInstancePassId) {
		parameters.push({
			name: "CarrierInstancePassId",
			value: carrierInstancePassId,
		});
	}

	const resp = await apiService.callV2(
		"[M2].[UpsertCarrierInstancePass]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error updating carrier instance pass");
	}

	return get(resp, "data.returnValue", null) as number;
};

export const getCarrierInstancePassHistory = async (
	params: { carrierInstanceId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "CarrierInstanceId", value: params.carrierInstanceId },
	];

	const resp = await apiService.callV2(
		"[M2].[GetCarrierInstancePassHistory]",
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as PassHistoryEntry[];
		return rows.map((row) => ({
			...row,
			StartedAt: row.StartedAt ? moment.utc(row.StartedAt).toDate() : null,
			EndedAt: row.EndedAt ? moment.utc(row.EndedAt).toDate() : null,
		})) as PassHistoryEntry[];
	} else {
		throw new Error(resp.message || "Error fetching pass history");
	}
};

export const deleteCarrierInstancePass = async (
	carrierInstancePassId: number,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "CarrierInstancePassId", value: carrierInstancePassId },
	];

	const resp = await apiService.callV2(
		"[M2].[DeleteCarrierInstancePass]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting carrier instance pass");
	}
};
